import { useCallback } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { GrandPrixLandingPage } from '@features/landing/grandprix/pages/GrandPrixLandingPage';
import { LeaderboardProvider } from '@features/landing/grandprix/providers/LeaderboardProvider';
import { LeaderboardClock, LeaderboardTillAge, LeaderboardType } from '@features/landing/grandprix/typings';
import { PremiumLinksProvider } from '@features/landing/shared/providers/PremiumLinksProvider';

export const Route = createFileRoute('/grandprix/')({
  component: RouteComponent,
  validateSearch: z.object({
    tillAge: z.enum(['11', '15', '19']).catch('11'),
    leaderboardType: z.enum(['open', 'girls']).catch('open'),
    clock: z.enum(['blitz', 'rapid']).catch('blitz'),
    showAll: z.boolean().optional(),
  }),
});

const SEARCH_NAVIGATE_OPTIONS = {
  resetScroll: false,
  replace: true,
};

function RouteComponent() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  //

  const onTillAgeChange = useCallback(
    (value: LeaderboardTillAge) => {
      navigate({
        search: (prev) => ({ ...prev, tillAge: value }),
        ...SEARCH_NAVIGATE_OPTIONS,
      });
    },
    [navigate],
  );

  const onClockChange = useCallback(
    (value: LeaderboardClock) => {
      navigate({
        search: (prev) => ({ ...prev, clock: value }),
        ...SEARCH_NAVIGATE_OPTIONS,
      });
    },
    [navigate],
  );

  const onLeaderboardTypeChange = useCallback(
    (value: LeaderboardType) => {
      navigate({
        search: (prev) => ({ ...prev, leaderboardType: value }),
        ...SEARCH_NAVIGATE_OPTIONS,
      });
    },
    [navigate],
  );

  //

  const onOpenModal = useCallback(() => {
    navigate({
      search: (prev) => ({ ...prev, showAll: true }),
      ...SEARCH_NAVIGATE_OPTIONS,
    });
  }, [navigate]);

  const onCloseModal = useCallback(() => {
    navigate({
      search: (prev) => {
        const { showAll, ...rest } = prev;
        return rest;
      },
      ...SEARCH_NAVIGATE_OPTIONS,
    });
  }, [navigate]);

  return (
    <PremiumLinksProvider>
      <LeaderboardProvider
        tillAgeParam={search.tillAge}
        onTillAgeParamChange={onTillAgeChange}
        clockParam={search.clock}
        onClockParamChange={onClockChange}
        leaderboardTypeParam={search.leaderboardType}
        onLeaderboardTypeParamChange={onLeaderboardTypeChange}
        showModalParam={Boolean(search.showAll)}
        onOpenModal={onOpenModal}
        onCloseModal={onCloseModal}
      >
        <GrandPrixLandingPage />
      </LeaderboardProvider>
    </PremiumLinksProvider>
  );
}
